import React, { FC, ReactElement, ReactNode, useCallback, useEffect, useRef, } from "react";
import { classNames, parseClasses, Button, ButtonColor, ButtonStyle, } from ".";

import "./style.scss";

export enum ModalType {
    Alert = 1,
    Confirm,
}

interface Props {
    title: string;
    type?: ModalType;

    // button types
    cancelLabel?: string;
    // cancelDisabled is just for the button in the footer;
    // there will *always* be an X in the header
    // note that this will visually hide the button, not disabled it
    cancelDisabled?: boolean;
    confirmLabel?: string;
    confirmDisabled?: boolean;
    dangerLabel?: string;
    dangerDisabled?: boolean;

    // additional CSS classes
    className?: string | string[];
    documentClassName?: string | string[];
    headerClassName?: string | string[];
    bodyClassName?: string | string[];
    footerClassName?: string | string[];

    // events
    onCancel: (args?: any) => any;
    onClose?: (args?: any) => any;
    onConfirm?: (args?: any) => any;
    onDanger?: (args?: any) => any;

    // extra action buttons
    extraActionButtons?: ReactElement[];

    // content
    children?: ReactNode;
}

export const Modal: FC<Props> = (props): ReactElement => {
    const {
        title,
        onCancel,
        children,
        className,
        documentClassName,
        headerClassName,
        bodyClassName,
    } = props;
    const ref = useRef<HTMLDivElement>(null);

    // escape handler
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const key = e.key.toLowerCase();

        switch (key) {
            case "escape":
                e.preventDefault();
                onCancel();
                break;

            default:
                break;
        }
    }, [onCancel]);

    // document css classes
    // we use classList.add / .remove for the document,
    // so we need the classes in an array, rather than a single string
    // because they'll ultimately be set via spread operator
    const documentCss = ["modal-open"];
    if (documentClassName) {
        const parsedDocumentCss = parseClasses(documentClassName).split(" ");
        documentCss.push(...parsedDocumentCss);
    }

    useEffect(() => {
        // mount
        document.body.classList.add(...documentCss);
        document.addEventListener("keydown", handleKeyDown);
        const timerId = window.setTimeout(() => ref.current && ref.current.classList.add("show"), 10);

        // unmount
        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener("keydown", handleKeyDown);
            document.body.classList.remove(...documentCss);
        };
    });

    // modal css classes
    const modalCss = classNames([
        "modal",
        "fade",
        "__modal__",
        parseClasses(className),
    ]);
    const headerCss = classNames([
        "modal-header",
        "__modalheader__",
        parseClasses(headerClassName),
    ]);
    const bodyCss = classNames([
        "modal-body",
        "__modalbody__",
        parseClasses(bodyClassName),
    ]);

    return (
        <div ref={ref} className={modalCss} tabIndex={-1}>
            <div className="modal-dialog  modal-dialog-scrollable">
                <div className="modal-content">
                    <div className={headerCss}>
                        <h5 className="modal-title">{title}</h5>
                        <Button
                            style={ButtonStyle.Close}
                            onClick={onCancel}
                        />
                    </div>

                    <div className={bodyCss}>
                        {children}
                    </div>

                    {_renderFooter(props)}
                </div>
            </div>
        </div>
    );
};

const _renderFooter = (props: Props): ReactElement => {
    const {
        cancelDisabled,
        cancelLabel,
        onCancel,
        onClose,
        type,
        footerClassName,
    } = props;
    const confirm = type === ModalType.Confirm;
    let justify = "";
    if (confirm) {
        justify = cancelDisabled ? "justify-content-center" : "justify-content-between";
    }

    const css = classNames([
        "modal-footer",
        "__modalfooter__",
        justify,
        parseClasses(footerClassName),
    ]);

    const closeStyle = confirm ? ButtonStyle.Outline : ButtonStyle.Default;
    const closeLabel = confirm ? (cancelLabel || "Cancel") : "Close";

    const cancelBtn = (
        <Button onClick={onClose || onCancel} style={closeStyle}>
            <span>{closeLabel}</span>
        </Button>
    );

    return (
        <div className={css}>
            {!cancelDisabled && cancelBtn}
            {confirm && renderActionButtons(props)}
        </div>
    );
};

const renderActionButtons = (props: Props): ReactElement => {
    return (
        <div className="__actionbuttons__">
            {renderExtraActionsButtons(props)}
            {renderDangerButton(props)}
            {renderConfirmButton(props)}
        </div>
    );
}

const renderExtraActionsButtons = (props: Props): ReactElement | null => {
    const { extraActionButtons, } = props;
    if (!extraActionButtons) {
        return null;
    }

    return (
        <div className="__extras__">
            {extraActionButtons}
        </div>
    );
};

const renderDangerButton = (props: Props): ReactElement | null => {
    const { dangerLabel, onDanger, } = props;

    if (!onDanger) {
        return null;
    }

    return (
        <Button onClick={onDanger} color={ButtonColor.Danger}>
            <span>{dangerLabel || "Delete"}</span>
        </Button>
    );
};

const renderConfirmButton = (props: Props): ReactElement | null => {
    const { confirmDisabled, confirmLabel, onConfirm, } = props;

    if (!onConfirm) {
        return null;
    }

    return (
        <Button onClick={onConfirm} disabled={confirmDisabled}>
            <span>{confirmLabel || "Confirm"}</span>
        </Button>
    );
};
